import React, { useEffect, useMemo, useState } from "react";
import {
  HiCalendarDays,
  HiChevronDown,
  HiLanguage,
  HiSquares2X2,
  HiStar,
  HiUser,
  HiXMark,
  HiOutlineFunnel,
} from "react-icons/hi2";
import "./AssessSections.css";

const initialFilters = {
  search: "",
  startDate: "",
  endDate: "",
  grade: "",
  language: "",
  subject: "",
  term: "",
  author: "",
  chapter: "",
  difficulty: "",
  status: "",
  examCategory: "",
  source: "",
  createdBy: "",
  year: "",
  sort: "created-desc",
};

const filterConfig = [
  { key: "grade", label: "Grade", icon: HiStar },
  { key: "language", label: "Language", icon: HiLanguage },
  { key: "subject", label: "Subject", icon: HiSquares2X2 },
  { key: "term", label: "Term", icon: HiSquares2X2 },
  { key: "author", label: "Author", icon: HiSquares2X2 },
  { key: "chapter", label: "Chapter", icon: HiSquares2X2 },
  { key: "difficulty", label: "Difficulty", icon: HiSquares2X2 },
  { key: "status", label: "Status", icon: HiSquares2X2 },
  { key: "examCategory", label: "Exam Category", icon: HiSquares2X2 },
  { key: "source", label: "Source", icon: HiSquares2X2 },
  { key: "createdBy", label: "Created By", icon: HiUser },
  { key: "year", label: "Year", icon: HiCalendarDays },
];

const getUniqueOptions = (items, key) =>
  [...new Set(items.map((item) => item[key]).filter(Boolean))].sort();

export default function AssessmentLibraryFilter({ items, onItemsChange }) {
  const [filters, setFilters] = useState(initialFilters);

  const options = useMemo(() => {
    return filterConfig.reduce((acc, filter) => {
      acc[filter.key] = getUniqueOptions(items, filter.key);
      return acc;
    }, {});
  }, [items]);

  const filteredItems = useMemo(() => {
    const normalizedSearch = filters.search.trim().toLowerCase();
    const startTime = filters.startDate ? new Date(filters.startDate).getTime() : null;
    const endTime = filters.endDate ? new Date(filters.endDate).getTime() : null;

    return [...items]
      .filter((item) => {
        const itemTime = new Date(item.createdAt).getTime();
        const matchesSearch =
          !normalizedSearch ||
          [
            item.name,
            item.status,
            item.subject,
            item.grade,
            item.author,
            item.examCategory,
            item.source,
            item.createdBy,
            item.year,
          ]
            .join(" ")
            .toLowerCase()
            .includes(normalizedSearch);

        const matchesDates =
          (!startTime || itemTime >= startTime) &&
          (!endTime || itemTime <= endTime);

        const matchesDropdowns = filterConfig.every((filter) => {
          const selectedValue = filters[filter.key];
          if (!selectedValue) return true;
          return item[filter.key] === selectedValue;
        });

        return matchesSearch && matchesDates && matchesDropdowns;
      })
      .sort((a, b) => {
        if (filters.sort === "created-asc") {
          return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
        }
        if (filters.sort === "title") {
          return a.name.localeCompare(b.name);
        }
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      });
  }, [filters, items]);

  useEffect(() => {
    onItemsChange(filteredItems);
  }, [filteredItems, onItemsChange]);

  const updateFilter = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setFilters(initialFilters);
  };

  return (
    <div className="assessment-filter">
      <div className="assessment-filter__top">
        <input
          type="search"
          placeholder="Search"
          value={filters.search}
          onChange={(event) => updateFilter("search", event.target.value)}
          aria-label="Search assessments"
        />
        <div className="assessment-filter__count">
          Number of Assessments <strong>{filteredItems.length}</strong>
        </div>
        <label className="assessment-filter__sort">
          <span>Sort By:</span>
          <select
            value={filters.sort}
            onChange={(event) => updateFilter("sort", event.target.value)}
            aria-label="Sort assessments"
          >
            <option value="created-desc">Created Date</option>
            <option value="created-asc">Oldest Date</option>
            <option value="title">Title</option>
          </select>
        </label>
      </div>

      <div className="assessment-filter__panel">
        <div className="assessment-filter__dates">
          <label>
            <span>Start Date:</span>
            <input
              type="date"
              value={filters.startDate}
              onChange={(event) => updateFilter("startDate", event.target.value)}
            />
          </label>
          <label>
            <span>End Date:</span>
            <input
              type="date"
              value={filters.endDate}
              onChange={(event) => updateFilter("endDate", event.target.value)}
            />
          </label>
        </div>

        <div className="assessment-filter__chips">
          {filterConfig.map((filter) => {
            const Icon = filter.icon;
            return (
              <label className="assessment-filter-chip" key={filter.key}>
                <Icon aria-hidden="true" />
                <select
                  value={filters[filter.key]}
                  onChange={(event) => updateFilter(filter.key, event.target.value)}
                  aria-label={filter.label}
                >
                  <option value="">{filter.label}</option>
                  {(options[filter.key] || []).map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
                <HiChevronDown aria-hidden="true" />
              </label>
            );
          })}
        </div>

        <div className="assessment-filter__actions">
          <button type="button" className="assessment-filter-btn assessment-filter-btn--clear" onClick={clearFilters}>
            <HiXMark aria-hidden="true" />
            Clear Filters
          </button>
          <button type="button" className="assessment-filter-btn assessment-filter-btn--apply">
            <HiOutlineFunnel aria-hidden="true" />
            Filter
          </button>
        </div>
      </div>
    </div>
  );
}
